import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";

import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Typography,
  makeStyles,
  useTheme,
  LinearProgress,
  Collapse,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import SnoozeIcon from "@material-ui/icons/Snooze";
import SettingsIcon from "@material-ui/icons/Settings";
import { Search } from "../../components/FormElements";
import { FleetContext } from "../../context";
import FleetMap from "./FleetMap";
import FleetTable from "./FleetTable";
import { PermissionedDisplay } from "../../components/Layout";
import Permissions from "../../permissions";
import { availableStates } from "../../constants";
import { getBoundingBox } from "../../services/map.service";
import { ScooterSVG } from "../../components/SVGHelpers";

const useStyles = makeStyles((theme) => ({
  head: {
    background: theme.palette.background.paper,
    maxWidth: "100%",
    margin: "0 auto",
    padding: theme.spacing(2, 2, 1, 2),
  },
  secondaryIcon: { color: theme.palette.text.secondary, marginLeft: theme.spacing(1) },
  chipIcon: {
    height: 12,
    width: 12,
    marginLeft: 12,
    marginRight: -4,
    borderRadius: "50%",
  },
  flexFloatRight: {
    marginLeft: "auto",
    order: 2,
  },
  textButton: {
    color: theme.palette.primary.button,
  },
  noVehiclesLabel: {
    background: theme.palette.background.paper,
    maxWidth: "100%",
    margin: "0 auto",
    padding: theme.spacing(0, 2, 0, 1),
  },
  table: { maxWidth: "100%", overflowX: "scroll" },
  typography: {
    color: theme.palette.text.primary,
  },
}));

const Fleet = () => {
  const DEFAULT_STATUSES = ["available", "reserved"];
  const REFRESH_TIMEOUT = 30;
  const {
    fleetVehicles,
    fetchVehiclesInFleet,
    setActiveFleet,
    activeFleet,
    geofences,
    fetchGeofences,
  } = useContext(FleetContext);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [vehiclesByStatus, setVehiclesByStatus] = useState(null);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [sortBy, setSortBy] = useState("target_state");
  const [orderBy, setOrderBy] = useState("asc");
  const [refreshTimeout, setRefreshTimeout] = useState(false);
  const { fleetId } = useParams();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    setActiveFleet(fleetId);

    const handleFetchVehiclesAndFences = async () => {
      setLoading(true);
      await Promise.all([fetchVehiclesInFleet(fleetId), fetchGeofences(fleetId)]);
      setLoading(false);
    };

    handleFetchVehiclesAndFences(fleetId);
  }, [fleetId]);

  useEffect(() => {
    if (fleetVehicles[fleetId] && fleetVehicles[fleetId].vehicles) {
      setVehiclesByStatus({ ...fleetVehicles[fleetId].vehicles });
    } else {
      setVehiclesList([]);
      setVehiclesByStatus(null);
    }
  }, [fleetVehicles, activeFleet]);

  useEffect(() => {
    if (!vehiclesByStatus) return;
    const newList = statuses.map((s) => vehiclesByStatus[s]).flat();
    setVehiclesList(sortVehiclesList(newList));
  }, [statuses, vehiclesByStatus]);

  useEffect(() => {
    setVehiclesList(sortVehiclesList());
  }, [sortBy, orderBy]);

  const sortVehiclesList = (list = null) => {
    if (!sortBy) return vehiclesList;
    const newList = !list ? [...vehiclesList] : list;
    if (sortBy === "shortcodes") {
      newList.sort((v1, v2) => {
        const vs1 = v1.shortcodes.length && v1.shortcodes[0].shortcode;
        const vs2 = v2.shortcodes.length && v2.shortcodes[0].shortcode;
        if (!vs1) return orderBy === "desc" ? 1 : -1;
        if (!vs2) return orderBy === "desc" ? -1 : 1;
        if (orderBy === "desc") return vs2 <= vs1 ? -1 : 1;
        return vs1 <= vs2 ? -1 : 1;
      });
    }
    newList.sort((v1, v2) => {
      if (!v1[sortBy]) return orderBy === "desc" ? 1 : -1;
      if (!v2[sortBy]) return orderBy === "desc" ? -1 : 1;
      if (orderBy === "desc") return v2[sortBy] <= v1[sortBy] ? -1 : 1;
      return v1[sortBy] <= v2[sortBy] ? -1 : 1;
    });
    return newList;
  };

  const toggleStatus = (status) => {
    if (status === "all") {
      if (statuses.length === Object.values(availableStates).length) {
        setStatuses([]);
      } else {
        setStatuses(Object.values(availableStates));
      }
    } else if (statuses.includes(status)) {
      setStatuses(statuses.filter((s) => s !== status));
    } else {
      setStatuses([...statuses, status]);
    }
  };

  const filterShortCodes = (text) => {
    if (!fleetVehicles[fleetId] || !fleetVehicles[fleetId].vehicles) return;
    const newVehiclesByStatus = {};
    const newStatuses = [];
    Object.entries(fleetVehicles[fleetId].vehicles).forEach(([status, vehicles]) => {
      const newVehicles = vehicles.filter(
        (v) =>
          !text ||
          (v.shortcodes &&
            v.shortcodes.some((sc) =>
              sc.shortcode
                ? sc.shortcode.toString().toLowerCase().includes(text.toLowerCase())
                : false,
            )),
      );
      newVehiclesByStatus[status] = newVehicles;
      if (newVehicles.length) newStatuses.push(status);
    });
    setVehiclesByStatus(newVehiclesByStatus);
    setStatuses(newStatuses);
  };

  const handleRefresh = () => {
    setRefreshTimeout(true);
    fetchVehiclesInFleet(fleetId);
    setTimeout(() => {
      setRefreshTimeout(false);
    }, REFRESH_TIMEOUT * 1000);
  };

  return (
    <>
      <Collapse in={loading}>
        <LinearProgress color="secondary" data-testid="linear-progress" />
      </Collapse>
      <Grid container spacing={2} className={classes.head}>
        <Grid item container spacing={1} xs={12}>
          <Grid item container xs={12} md={6}>
            <Typography variant="h5">
              {activeFleet && `${activeFleet.name} ${t("Fleet.Head.Fleet")}`}
            </Typography>
            <Grid item>
              <PermissionedDisplay permission={[Permissions.readFleets]}>
                <IconButton
                  aria-label="Settings"
                  size="small"
                  className={classes.secondaryIcon}
                  onClick={() => history.push(`/fleets/${fleetId}/settings`)}
                >
                  <SettingsIcon />
                </IconButton>
              </PermissionedDisplay>
              <PermissionedDisplay
                permission={[
                  Permissions.feature.otaPage,
                  Permissions.readFirmware,
                  Permissions.writeFirmware,
                ]}
              >
                <IconButton
                  aria-label="OTA"
                  size="small"
                  className={classes.secondaryIcon}
                  onClick={() => history.push(`/fleets/${fleetId}/ota`)}
                >
                  <ScooterSVG
                    viewBox="0 0 20 20"
                    width="20"
                    height="20"
                    color="#A9ACB6"
                  />
                </IconButton>
              </PermissionedDisplay>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            container
            direction="row"
            justify="flex-end"
            alignItems="baseline"
          >
            {fleetVehicles[fleetId] && vehiclesByStatus && (
              <Grid item>
                <Typography variant="caption" className={classes.typography}>
                  {`${t("Fleet.Head.LastUpdated")} ${moment(fleetVehicles[fleetId].updated).format(
                    "hh:mm:ss",
                  )}`}
                </Typography>
              </Grid>
            )}
            <Grid item>
              <IconButton
                aria-label="Refresh"
                edge="end"
                size="small"
                className={classes.secondaryIcon}
                onClick={handleRefresh}
                disabled={refreshTimeout}
              >
                {refreshTimeout ? <SnoozeIcon /> : <RefreshIcon />}
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container spacing={1} xs={12}>
          <Grid item xs={12} md={6} lg={3}>
            <Search onChange={filterShortCodes} debounce={350} />
          </Grid>
          <Grid item xs={12} md={6} lg={9} container direction="row" justify="flex-end">
            <Grid item>
              <Typography variant="caption" display="inline">
                {t("Fleet.Head.ScooterCount")}:
              </Typography>
              {fleetVehicles[fleetId] && vehiclesByStatus && (
                <Typography variant="subtitle2" display="inline">
                  {` ${vehiclesList.length}/${fleetVehicles[fleetId].count}`}
                </Typography>
              )}
              {!vehiclesList.length && (
                <Typography variant="subtitle2" display="inline">
                  {t("0/0")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item container spacing={1} xs={12} alignItems="baseline">
          {Object.entries(availableStates).map(([i, status]) => (
            <Grid item key={i}>
              <Chip
                label={
                  t(`Vehicles.Detail.Statuses.${status}`) +
                  (vehiclesByStatus && vehiclesByStatus[status]
                    ? ` (${vehiclesByStatus[status].length})`
                    : " (0)")
                }
                style={
                  statuses.includes(status)
                    ? { background: theme.palette.background.selected }
                    : { background: theme.palette.background.default }
                }
                clickable
                icon={
                  <div
                    className={classes.chipIcon}
                    style={{ background: theme.palette.statusColors[status] }}
                  ></div>
                }
                onClick={() => toggleStatus(status)}
              />
            </Grid>
          ))}
          <Grid item className={classes.flexFloatRight}>
            <Button className={classes.textButton} onClick={() => toggleStatus("all")}>
              {t("Fleet.Head.ToggleAll")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Box>
        <FleetMap
          {...{ vehiclesList, fleetId }}
          updateOnChangeTo={statuses}
          defaultBounds={getBoundingBox(activeFleet.bounding_box)}
          geofences={
            geofences && geofences.length ? geofences.filter((g) => g.fleetId === fleetId) : null
          }
        />
      </Box>
      {(!fleetVehicles[fleetId] || !vehiclesByStatus) && (
        <Grid container spacing={2} className={classes.noVehiclesLabel}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" gutterBottom>
              {t("No vehicles found in fleet!")}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Box className={classes.table}>
        <FleetTable
          {...{ vehiclesList, orderBy, sortBy, setOrderBy, setSortBy, fleetId, activeFleet }}
        />
      </Box>
      {loading && (
        <Grid item xs={12} style={{ padding: "0px" }}>
          <LinearProgress color="secondary" />
        </Grid>
      )}
    </>
  );
};

export default Fleet;
